import api from './api';
import { User } from '../types';

interface LoginResponse {
  user: User;
  token: string;
}

interface RegistrationResponse {
  pending: true;
  email: string;
  pendingId: string;
}

export const login = async (email: string, password: string): Promise<LoginResponse> => {
  try {
    const response = await api.post('/auth/login', { email, password });
    return response.data;
  } catch (err: any) {
    if (err.response) {
      const loginError = new Error(err.response.data?.message || 'Authentication failed') as Error & { code?: string };
      if (err.response.data?.error === 'email_verification_required') {
        loginError.code = 'email_verification_required';
      }
      throw loginError;
    } else if (err.request) {
      throw new Error('Server connection error. Please try again later.');
    } else {
      throw new Error('Login request failed: ' + (err.message || 'Unknown error'));
    }
  }
};

export const register = async (name: string, email: string, password: string): Promise<RegistrationResponse> => {
  try {
    const response = await api.post('/auth/register', { name, email, password }, {
      headers: { 'X-Signup-Flow': 'email-code-v1' },
    });
    return response.data;
  } catch (err: any) {
    if (err.response) {
      throw new Error(err.response.data?.message || 'Registration failed');
    } else if (err.request) {
      throw new Error('Server connection error. Please try again later.');
    } else {
      throw new Error('Registration request failed: ' + (err.message || 'Unknown error'));
    }
  }
};

export const verifyEmail = async (pendingId: string, code: string): Promise<LoginResponse> => {
  try {
    const response = await api.post('/auth/verify-email', { pendingId, code });
    return response.data;
  } catch (err: any) {
    if (err.response) {
      throw new Error(err.response.data?.message || 'Could not verify your email. Check the code and try again.');
    } else if (err.request) {
      throw new Error('Server connection error. Please try again later.');
    } else {
      throw new Error('Email verification failed: ' + (err.message || 'Unknown error'));
    }
  }
};

export const resendVerification = async (pendingId: string): Promise<{ message: string }> => {
  try {
    const response = await api.post('/auth/resend-verification', { pendingId });
    return response.data;
  } catch (err: any) {
    if (err.response) {
      throw new Error(err.response.data?.message || 'Could not resend the verification code. Please try again.');
    } else if (err.request) {
      throw new Error('Server connection error. Please try again later.');
    } else {
      throw new Error('Could not resend the verification code: ' + (err.message || 'Unknown error'));
    }
  }
};

export const recoverVerification = async (email: string, password: string): Promise<{ pendingId: string; message: string }> => {
  try {
    const response = await api.post('/auth/recover-verification', { email, password });
    return response.data;
  } catch (err: any) {
    if (err.response) {
      throw new Error(err.response.data?.message || 'Could not request a verification code. Please try again.');
    } else if (err.request) {
      throw new Error('Server connection error. Please try again later.');
    } else {
      throw new Error('Could not request a verification code: ' + (err.message || 'Unknown error'));
    }
  }
};

export const logout = async (): Promise<void> => {
  try {
    await api.post('/auth/logout');
  } catch {
    // We don't throw errors on logout to ensure the user is always logged out
    // even if the server request fails
  }
};

export const getCurrentUser = async (): Promise<User> => {
  try {
    const response = await api.get('/auth/me');
    return response.data;
  } catch (err: any) {
    if (err.response && err.response.status === 401) {
      throw new Error('Not authenticated');
    } else {
      throw new Error('Failed to get user profile: ' + (err.message || 'Unknown error'));
    }
  }
};

export const handleOAuthCallback = async (code: string, provider: 'google' | 'microsoft'): Promise<LoginResponse> => {
  // The OAuth callback is handled by the backend redirect
  // This is just a utility function for handling token from the redirect
  throw new Error('OAuth callback should be handled by redirect');
};